import { MutationType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import cloneDeep from 'lodash/cloneDeep';
import { Rule } from '../base/mutation-rules';
import { BaseMutation } from '../base/base-mutation';
import { DataType, SHAPE_DATA_TYPES, SLIDE_PAGE_DATA_TYPES } from '../../data';
import { getSingleShapeKeyFrame } from '../../keyframe/get-shape-keyframe';
import type { PresentationDataModel } from '../../model/presentation-data-model';
import type { MaybeError } from '../base/base-mutation';
import type { IValidMutationData, IValidUngroupMutationData } from '../base/valid-mutation-data';

export class UngroupMutation extends BaseMutation<PresentationDataModel, IValidUngroupMutationData> {
    public readonly type: MutationType = MutationType.Ungroup;

    public apply(presentation: PresentationDataModel) {
        const { groupShapeId } = this.data;
        const groupShape = presentation.store.models.get(groupShapeId, DataType.GroupShape);
        const parentGroupShape = groupShape?.parent;
        assert(parentGroupShape?.type === DataType.GroupShape, 'groupShape parent is not a group shape');

        parentGroupShape.store.ungroupChildGroupById(groupShapeId);
    }

    public invert(presentation: PresentationDataModel): IValidMutationData[] {
        const { pageId, groupShapeId, parentId, index } = this.data;
        const page = presentation.store.models.get(pageId, ...SLIDE_PAGE_DATA_TYPES);
        assert(page, 'page should exist');
        const shape = presentation.store.models.get(groupShapeId, ...SHAPE_DATA_TYPES);
        assert(shape, 'shape should exist');

        const mutations: IValidMutationData[] = [];
        const context = {
            presentation: presentation.store.data,
            page: page.store.data,
            idFactory: (oldId: string) => oldId,
        };
        mutations.push();

        return getSingleShapeKeyFrame(cloneDeep(shape.store.data), context, parentId, index);
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, groupShapeId, parentId, index, shapeIds } = this.data;

        return Rule.every(
            Rule.shape.should.existInPage(presentation, groupShapeId, pageId),
            Rule.shape.should.existInPage(presentation, parentId, pageId),
            Rule.shape.is.typeOf(presentation, groupShapeId, DataType.GroupShape),
            Rule.shape.should.matchShapeListInPosition(presentation, parentId, index, [groupShapeId]),
            Rule.shape.should.matchShapeListInPosition(presentation, groupShapeId, 0, shapeIds),
        );
    }
}
